import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded'
import { Box, Typography } from '@mui/material'
import clsx from 'clsx'
import moment from 'moment'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { deleteSeminarLessonMultimedia, getRegisteredUsers, setViewSeminarLesson } from '../../../../../../redux/actions/seminarlessons'
import { IRootState } from '../../../../../../redux/types'
import SupportFile from '../../../../../shared/components/frontoffice/SupportFile'
import 'react-dropzone-uploader/dist/styles.css'
import {
  BannerHeroDetail,
  BtnLinkDetail,
  ChatDetail,
  ContainerDetail,
  LimitEmbed,
  LinkPrimary,
  TextReadyDetail,
  VideoDetail
} from '../../../../../shared/styles/SeminarGlobalStyles'
import { VipContainerHeroDetail } from '../../../../../shared/styles/VipStyles'

type SeminarLessonDetailProps = {
  handleOpen: (state: boolean) => void
}

function SeminarLessonDetail({ handleOpen }: SeminarLessonDetailProps) {
  const { t } = useTranslation(['global'])
  const dispatch = useDispatch()
  const { seminarSelected } = useSelector((state: IRootState) => state.seminar)
  const { seminarlesson_selected } = useSelector((state: IRootState) => state.seminarlesson)
  const { user: authUser, isAuth } = useSelector((state: IRootState) => state.auth)

  let isEmbed: boolean = seminarlesson_selected.type === 0

  const myRef = useRef(null)
  const scrollToRef = (ref: any) =>
    window.scrollTo({
      top: ref?.current?.offsetTop ?? 800,
      left: 0,
      behavior: 'smooth'
    })
  const executeScroll = () => scrollToRef(myRef)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (isAuth && authUser !== null) dispatch(getRegisteredUsers(seminarlesson_selected.id))
  }, [isAuth])

  useEffect(() => {
    if (seminarlesson_selected !== null && seminarlesson_selected.isRegistered === null) {
      handleOpen(true)
    }
  }, [seminarlesson_selected])

  const handleDownloadMediaFileBlock = (fileName: string) => {
    window.open(`${import.meta.env.VITE_API_URL}/seminarlessons/download/${fileName}`)
  }

  const handleDeleteMediaFileBlock = (seminarlessonmultimedia_id: number) => {
    let index = seminarlesson_selected.multimedia.findIndex((multimedia: any) => multimedia.id === seminarlessonmultimedia_id)
    seminarlesson_selected.multimedia.splice(index, 1)
    dispatch(setViewSeminarLesson(seminarlesson_selected))
    dispatch(deleteSeminarLessonMultimedia(seminarlessonmultimedia_id))
  }

  return (
    <>
      <BannerHeroDetail
        className={clsx(
          isEmbed && 'wraped',
          authUser !== null &&
          (seminarlesson_selected.datetime === null ||
            moment(seminarlesson_selected.datetime).subtract(30, 'minutes').isSameOrBefore(moment())) &&
          'hide'
        )}
      >
        <Typography variant="h4" align="left">
          {seminarlesson_selected.title}
        </Typography>
        {seminarlesson_selected.datetime && <Typography>{`${moment(seminarlesson_selected.datetime).format('LLLL')}`}</Typography>}
        <Box mx="auto" display="flex" flexDirection="column" gap={16} mt={5}>
          {seminarlesson_selected.isRegistered !== null && authUser !== null ? (
            <ContainerDetail maxWidth={960} width="100%">
              <Typography align="center" variant="h4">
                {t('you-already-booked-ticket')}
              </Typography>
              <TextReadyDetail>
                <Typography>
                  {t('the-webinar-bee-sent')} <b>{authUser.email}</b>
                </Typography>
                <Typography>
                  {t('see-you-seminar')}, {authUser.name}!
                </Typography>
              </TextReadyDetail>
              <LinkPrimary variant="text" onClick={executeScroll} endIcon={<ArrowDownwardRounded />}>
                <Typography>{t('more-info')}</Typography>
              </LinkPrimary>
            </ContainerDetail>
          ) : (
            <>
              <BtnLinkDetail
                variant="contained"
                color="primary"
                onClick={() => {
                  handleOpen(true)
                }}
              >
                {t('book-my-ticket')}
              </BtnLinkDetail>

              <LinkPrimary variant="text" onClick={executeScroll} endIcon={<ArrowDownwardRounded />}>
                <Typography>{t('more-info')}</Typography>
              </LinkPrimary>
            </>
          )}
        </Box>
        {seminarlesson_selected.multimedia.length > 0 && (
          <Box mt={4} mb={6}>
            <Typography variant="subtitle1" color="primary">
              Material de la lección
            </Typography>
            <Box display="flex" mt={3} gap={16} flexDirection="row">
              {seminarlesson_selected.multimedia.map((multimedia: any) => (
                <SupportFile
                  key={uuidv4()}
                  data={multimedia}
                  type="seminarlessons"
                  vipContent={seminarSelected.visibility === 1}
                  hasaccess={
                    seminarSelected.visibility === 1 &&
                    authUser &&
                    (authUser.vip_lines.includes(seminarSelected.line_id) ||
                      (seminarSelected.line_id === null && authUser.vip_brands.includes(seminarSelected.brand_id)) ||
                      [1, 2, 3, 5].includes(authUser.role_id))
                  }
                  handleDownload={handleDownloadMediaFileBlock}
                  handleDelete={handleDeleteMediaFileBlock}
                />
              ))}
            </Box>
          </Box>
        )}
      </BannerHeroDetail>
      <VipContainerHeroDetail>
        {isEmbed ? (
          <LimitEmbed maxWidth={'lg'} style={{ height: 'max-content' }}>
            <div dangerouslySetInnerHTML={{ __html: seminarlesson_selected.content }} />
          </LimitEmbed>
        ) : (
          <>
            <VideoDetail>
              <iframe
                style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
                allow="autoplay"
                src={seminarlesson_selected.content}
                frameBorder={0}
                allowFullScreen={true}
                title="canva"
              />
            </VideoDetail>
            <ChatDetail>
              <iframe
                style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
                src={seminarlesson_selected.extracontent}
                frameBorder={0}
                title="chat"
              />
            </ChatDetail>
          </>
        )}
      </VipContainerHeroDetail>
    </>
  )
}

export default SeminarLessonDetail
